import type { ConnectorId } from "./connectors/types";
import {
  getSimulationModel,
  type SimulationCheckpoint,
  type SimulationModelId,
  type SimulationRun,
} from "./simulations";
import type { SystemId } from "./systems";

export type TradeAction = "Buy" | "Sell" | "Hedge" | "No trade";

export type TradeCandidate = {
  id: string;
  instrument: string;
  action: TradeAction;
  status: "simulation only";
  horizonDays: number;
  thesis: string;
  entryCondition: string;
  invalidation: string;
  marketEdge: string;
  expectedReturnPct: number | null;
  probabilityOfLossPct: number | null;
  expectedShortfallPct: number | null;
  maximumAllocationPct: 0;
};

export type ProjectionTrajectory = {
  id: string;
  title: string;
  summary: string;
  probabilityBps: number;
  sampleCount: number;
  supportingConditions: string[];
  invalidatingConditions: string[];
  checkpoints: SimulationCheckpoint[];
};

export type ProjectionSet = {
  id: string;
  family: string;
  title: string;
  target: string;
  partitionRule: string;
  horizonDays: number;
  asOf: string | null;
  simulationModelId: SimulationModelId;
  simulationRunId: string;
  modelVersion: string;
  particleCount: number;
  modelConfidence: number;
  calibration: SimulationRun["calibration"];
  systemIds: SystemId[];
  requiredSourceIds: ConnectorId[];
  sourceGaps: string[];
  sharedCheckpoints: SimulationCheckpoint[];
  trajectories: ProjectionTrajectory[];
  tradeCandidates: TradeCandidate[];
};

const presentation: Record<SimulationModelId, { id: string; family: string; title: string }> = {
  "btc-jump-diffusion": {
    id: "btc-30-day-return",
    family: "Market state",
    title: "BTC 30-day return",
  },
  "financial-regime-transition": {
    id: "financial-transmission-90-day",
    family: "Financial conditions",
    title: "Financial transmission",
  },
  "institutional-rule-transition": {
    id: "digital-asset-rules-180-day",
    family: "Institutional rules",
    title: "Digital-asset rule regime",
  },
  "strategic-interaction-network": {
    id: "strategic-interaction-180-day",
    family: "Strategic interaction",
    title: "Cross-polity interaction",
  },
};

function metric(run: SimulationRun, label: string) {
  return run.metrics.find((entry) => entry.label === label)?.value ?? null;
}

function btcTradeCandidate(run: SimulationRun): TradeCandidate {
  const expectedReturnPct = metric(run, "Mean return");
  const probabilityOfLossPct = metric(run, "Loss probability");
  const expectedShortfallPct = metric(run, "5% expected shortfall");
  const action: TradeAction = expectedReturnPct === null || Math.abs(expectedReturnPct) < 0.25
    ? "No trade"
    : expectedReturnPct > 0
      ? "Buy"
      : "Sell";

  return {
    id: "btc-directional-expression",
    instrument: "BTC/USD",
    action,
    status: "simulation only",
    horizonDays: 30,
    thesis: action === "No trade"
      ? "The baseline ensemble does not produce enough directional separation to express a side."
      : `${action} is the directional expression implied by the ensemble mean, not an executable recommendation.`,
    entryCondition:
      "Remain disabled until point-in-time replay, out-of-sample calibration, transaction costs, liquidity, and an independent risk limit are present.",
    invalidation:
      "A source-state change, parameter revision, failed calibration, or loss of input coverage invalidates this expression.",
    marketEdge: "Unproven — no calibrated benchmark, cost model, or market-implied comparison.",
    expectedReturnPct,
    probabilityOfLossPct,
    expectedShortfallPct,
    maximumAllocationPct: 0,
  };
}

function unmappedTradeCandidate(run: SimulationRun, title: string): TradeCandidate {
  return {
    id: `${run.modelId}-unmapped-expression`,
    instrument: "No mapped instrument",
    action: "No trade",
    status: "simulation only",
    horizonDays: getSimulationModel(run.modelId)?.horizonDays ?? 0,
    thesis: `${title} is a system-state forecast. It has no validated instrument/payoff mapping yet.`,
    entryCondition: "Define an instrument, payoff function, market benchmark, cost model, and independent risk constraints.",
    invalidation: "Any trade expression remains invalid until the mapping is point-in-time testable.",
    marketEdge: "Not calculated — projection probability alone is not trade edge.",
    expectedReturnPct: null,
    probabilityOfLossPct: null,
    expectedShortfallPct: null,
    maximumAllocationPct: 0,
  };
}

export function buildProjectionSets(runs: SimulationRun[]): ProjectionSet[] {
  return runs.flatMap((run) => {
    const model = getSimulationModel(run.modelId);
    if (!model) return [];
    const labels = presentation[run.modelId];
    const outcomeById = new Map(model.outcomes.map((outcome) => [outcome.id, outcome]));
    const trajectories = run.outcomes.flatMap((result) => {
      const definition = outcomeById.get(result.id);
      if (!definition) return [];
      return [{
        id: definition.id,
        title: definition.title,
        summary: definition.summary,
        probabilityBps: result.probabilityBps,
        sampleCount: result.sampleCount,
        supportingConditions: definition.supportingConditions,
        invalidatingConditions: definition.invalidatingConditions,
        checkpoints: definition.checkpoints,
      }];
    });

    return [{
      id: labels.id,
      family: labels.family,
      title: labels.title,
      target: model.target,
      partitionRule: model.partitionRule,
      horizonDays: model.horizonDays,
      asOf: run.asOf,
      simulationModelId: model.id,
      simulationRunId: run.id,
      modelVersion: run.modelVersion,
      particleCount: run.particleCount,
      modelConfidence: run.modelConfidence,
      calibration: run.calibration,
      systemIds: model.systemIds,
      requiredSourceIds: model.requiredSourceIds,
      sourceGaps: model.sourceGaps,
      sharedCheckpoints: model.sharedCheckpoints,
      trajectories,
      tradeCandidates: [model.id === "btc-jump-diffusion"
        ? btcTradeCandidate(run)
        : unmappedTradeCandidate(run, labels.title)],
    }];
  });
}

export function projectionTerminalCount(sets: ProjectionSet[]) {
  return sets.reduce((total, set) => total + set.trajectories.length, 0);
}

export function getProjectionById(sets: ProjectionSet[], id: string) {
  const set = sets.find((candidate) => candidate.id === id);
  if (set) return { set, trajectory: null };
  for (const candidate of sets) {
    const trajectory = candidate.trajectories.find((item) => item.id === id);
    if (trajectory) return { set: candidate, trajectory };
  }
  return null;
}
